<template>
  <component
    :is="htmlElement"
    v-tooltip="{
      shown: tooltipIsOpen,
      content: tooltipContent,
      html: true
    }"
    :href="href"
    :class="[
      appearance,
      (appearance === 'button') ? `${appearance}--${color}` : '',
      (appearance === 'button') ? `${appearance}--${decoration}` : '',
      block ? `${appearance}--block` : '',
      icon !== undefined ? `${appearance}--with-icon` : '',
      isIconOnly ? `${appearance}--icon-only` : '',
      animateIcon ? '--animate-rotate-45' : '',
      htmlElement === 'button' && (disabled || ariaDisabled) ? 'button--disabled' : ''
    ]"
    :type="htmlElement === 'button' ? buttonType : null"
    :role="((htmlElement === 'button' && role === 'button') || buttonType === 'submit') ? null : role"
    :disabled="htmlElement === 'button' ? disabled : null"
    :aria-disabled="ariaDisabled"
    :aria-label="ariaLabel"
    v-bind="attr"
    @click="onClick($event)"
    @mousedown="onMouseDown($event)"
    @keyup.esc="onKeyUpEsc($event)"
  >
    <span
      v-if="icon !== undefined"
      class="icon-container"
    >
      <svg-icon
        :class="[`icon icon-${icon}`, iconClass !== undefined ? iconClass : '']"
        :name="icon"
        aria-hidden="true"
        focusable="false"
      />
    </span>
    <span
      v-if="!isIconOnly"
      :class="`${appearance}__text`"
    >
      <slot />
    </span>
  </component>
</template>

<script>
import { hideAllPoppers } from 'floating-vue'
import { getDefaultSlotTextContent } from '@/helpers/helpers.js'

export default {
  name: 'BasicControl',
  props: {
    appearance: {
      type: String,
      default: 'button' // 'button' or 'link'
    },
    animateIcon: {
      type: Boolean,
      default: false
    },
    href: {
      type: String,
      default: undefined
    },
    iconOnly: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'primary'
    },
    icon: {
      type: String,
      default: undefined
    },
    iconClass: {
      type: String,
      default: undefined
    },
    decoration: {
      type: String,
      default: 'filled' // 'filled' or 'outlined'
    },
    disabled: {
      type: Boolean,
      default: undefined
    },
    ariaDisabled: {
      type: Boolean,
      default: undefined
    },
    attr: {
      type: Object,
      default: undefined // do not use title in here, use the title prop instead
      // when using aria-describedby keep in mind:
      // currently on controls that are icon-only the aria-describedby will be overritten by the v-tooltip which adds an own aria-describedby :(
      // e.g. if our modal submit buttons would be icon-only then their submitDisabledHint would just diabbpear because of their tooltip!
    },
    buttonType: {
      type: String,
      default: 'button'
    },
    role: {
      type: String,
      default: undefined
    },
    title: {
      type: String,
      default: null
    },
    propagation: {
      type: Boolean,
      default: true
    }
  },
  emits: [
    'onClick',
    'onMouseDown',
    'onEsc',
    'on-click',
  ],
  data () {
    return {
      tooltipIsOpen: false,
      mouseleaveTimer: false,
      documentEventListener: false
    }
  },
  computed: {
    htmlElement () {
      return this.href !== undefined ? 'a' : 'button'
    },
    isIconOnly () {
      return (this.icon !== undefined) && this.iconOnly
    },
    defaultSlotText () {
      return this.$slots.default && getDefaultSlotTextContent(this.$slots.default()) || ''
    },
    // label is the text that will be used as content of a tooltip or as aria-label
    label () {
      // the title attribute will be used primarily, afterwards the default slot text will be considered
      return (this.isIconOnly ? (this.title || this.defaultSlotText) : this.title)
    },
    tooltipContent () {
      if (this.label !== null) {
        // the tooltip is visible information only.
        // the screenreader gets information from aria-label of the element the tooltip is attached to
        return `<span aria-hidden="true">${this.label}</span>`
      } else {
        return null
      }
    },
    // 1. if there is no visible label text for the control (aka. only icon) then an default ariaLabel will be applied
    // 2. attention, this ariaLabel can be overritten by using :attr="{'aria-label': 'some label'}" or :aria-label="'some label'"
    ariaLabel () {
      // in case of a control with text
      return this.isIconOnly ? (this.label) : null
    },
  },
  mounted () {
    document.addEventListener('keydown', (event) => {
      const keycode = (event.keyCode ? event.keyCode : event.which)
      if (keycode === 27) {
        hideAllPoppers()
      }
    })
  },
  methods: {
    onClick (event) {
      if (this.htmlElement === 'a') {
        event.preventDefault()
      }

      if (!this.propagation) {
        event.stopPropagation()
      }

      this.$emit('onClick', event)
    },
    onMouseDown (event) {
      if (!this.propagation) {
        event.stopPropagation()
      }

      this.$emit('onMouseDown', event)
    },
    onKeyUpEsc (event) {
      // if there is an open tooltip the ESC should only close this tooltip, not triggering anything else
      if (document.querySelector('.v-popper__popper--shown')) {
        event.stopPropagation();
      } else {
        this.$emit('onEsc', event)
      }
    }
  }
}
</script>
